/**
 * Wrapper Capacitor unifié.
 *
 * Les plugins sont importés normalement afin d'être inclus dans le bundle
 * web/native. Les fonctions restent des no-op silencieuses hors wrapper
 * Capacitor natif.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import { ScreenOrientation, type OrientationLockType } from '@capacitor/screen-orientation';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

interface NativeBackgroundPlugin {
  setColor(options: { color: string }): Promise<void>;
}

const NativeBackground = registerPlugin<NativeBackgroundPlugin>('NativeBackground');

/** Feutre (moyenne de felt-green.jpg). */
export const NATIVE_BG_FELT = '#164b25';
/** Chrome sombre (hors tapis). */
export const NATIVE_BG_APP_DARK = '#0b141a';
/** Chrome clair (hors tapis). */
export const NATIVE_BG_APP_LIGHT = '#f4f1ec';

type Platform = 'ios' | 'android' | 'web';

interface CapacitorState {
  initialized: boolean;
  available: boolean;
  platform: Platform;
}

const state: CapacitorState = {
  initialized: false,
  available: false,
  platform: 'web',
};

let initPromise: Promise<void> | null = null;

/**
 * À appeler une fois au démarrage de l'app (dans main.tsx).
 * Détecte si Capacitor est présent. La promesse est partagée afin que les
 * appels arrivant trop tôt (ex: useOrientationLock) attendent l'init.
 */
export async function initPlatform(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    state.initialized = true;
    state.available = Capacitor.isNativePlatform();

    const detected = Capacitor.getPlatform();
    state.platform = (detected === 'ios' || detected === 'android') ? detected : 'web';

    if (state.available) {
      document.documentElement.dataset.native = state.platform;
    }

    // Les onglets restent montés (display:none) : un lock paysage posé
    // pendant une partie ne doit pas survivre au lancement suivant.
    if (state.available) {
      try {
        await ScreenOrientation.unlock();
      } catch {
        // ignore : non supporté
      }
    }
  })();

  return initPromise;
}

/** Attend l'initialisation Capacitor. Utile avant tout appel plugin. */
export function platformReady(): Promise<void> {
  return initPlatform();
}

/** True si l'app tourne dans un wrapper Capacitor natif (iOS/Android). */
export function isNative(): boolean {
  return state.available;
}

/** Plateforme courante : 'ios', 'android' ou 'web'. */
export function getPlatform(): Platform {
  return state.platform;
}

/**
 * Verrouille l'orientation de l'écran. No-op sur web (les navigateurs
 * exigent fullscreen + permissions, pas pratique pour un jeu).
 *
 * @param mode 'portrait' | 'landscape' | 'any'
 */
export async function lockOrientation(
  mode: 'portrait' | 'landscape' | 'any',
): Promise<void> {
  await platformReady();
  if (!state.available) return;

  try {
    if (mode === 'any') {
      await ScreenOrientation.unlock();
    } else {
      await ScreenOrientation.lock({ orientation: mode as OrientationLockType });
    }
  } catch {
    // ignore : non supporté
  }
}

/**
 * Configure le style de la barre de statut native (iOS / Android).
 * Sur web, no-op.
 */
export async function setStatusBarStyle(
  style: 'light' | 'dark',
  backgroundColor?: string,
): Promise<void> {
  await platformReady();
  if (!state.available) return;

  try {
    // Capacitor : Style.Light = texte sombre (fond clair) ;
    // Style.Dark = texte clair (fond sombre).
    await StatusBar.setStyle({
      style: style === 'light' ? Style.Light : Style.Dark,
    });
    if (backgroundColor) {
      await StatusBar.setBackgroundColor({ color: backgroundColor });
    }
  } catch {
    // ignore : non supporté
  }
}

/**
 * Vibration générique. Délègue à navigator.vibrate sur web,
 * et à @capacitor/haptics sur natif (utiliser utils/haptics.ts pour
 * les feedbacks haptiques nuancés).
 */
export function vibrate(pattern: number | number[] = 20): void {
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // ignore
  }
}

/**
 * Ouvre une URL externe.
 *
 * - Sur web : window.open avec sécurité noopener
 * - Sur natif : si @capacitor/browser est disponible, l'utilise pour ouvrir
 *   en navigateur in-app (Safari/Chrome système). Sinon fallback window.open.
 *
 * Indispensable en mode reader iOS/Android (Phase 4) pour rediriger vers
 * le tunnel Stripe Checkout sans heurter les règles App Store/Play Store.
 */
export async function openExternalUrl(url: string): Promise<void> {
  await platformReady();

  if (state.available) {
    try {
      // Import dynamique pour ne pas casser le bundle si le plugin n'est pas installé
      const mod = await import('@capacitor/browser').catch(() => null);
      if (mod && mod.Browser) {
        await mod.Browser.open({ url });
        return;
      }
    } catch {
      // fallback
    }
  }

  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Récupère l'identifiant d'installation natif (Capacitor Device.getId).
 * Sur web : retourne null (le fingerprint web est calculé séparément via
 * FingerprintJS dans lib/deviceFingerprint.ts).
 */
export async function getNativeDeviceId(): Promise<string | null> {
  await platformReady();
  if (!state.available) return null;

  try {
    const mod = await import('@capacitor/device').catch(() => null);
    if (!mod || !mod.Device) return null;
    const info = await mod.Device.getId();
    return info?.identifier || null;
  } catch {
    return null;
  }
}

const NATIVE_VIEWPORT =
  'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, viewport-fit=cover';

function lockNativeViewport(): void {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  meta.setAttribute('content', NATIVE_VIEWPORT);
}

if (typeof document !== 'undefined' && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
  document.documentElement.dataset.native = 'ios';
  lockNativeViewport();
}

function resetScrollAfterKeyboard(): void {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/**
 * Configure le clavier natif (Android : éviter resize de la webview).
 * iOS : empêche le zoom focus des inputs < 16px de rester collé après blur.
 */
export async function configureKeyboard(): Promise<void> {
  await platformReady();
  if (!state.available) return;

  try {
    if (state.platform === 'ios') {
      lockNativeViewport();
      await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
      await Keyboard.addListener('keyboardDidHide', resetScrollAfterKeyboard);
    }
  } catch {
    // ignore
  }
}

/**
 * Aligne le texte de la barre de statut sur `data-theme`.
 * (Capacitor viewDidAppear peut réappliquer capacitor.config ; on rappelle
 * cette fonction juste après, et au retour au premier plan.)
 */
export function applyStatusBarFromTheme(): void {
  const theme = document.documentElement.getAttribute('data-theme');
  const light = theme === 'light';
  void setStatusBarStyle(light ? 'light' : 'dark', light ? '#ffffff' : '#0b141a');
}

/**
 * Couleur de la WebView native (bandes de rotation).
 * `felt` sur le tapis, sinon le chrome du thème courant.
 */
export async function setNativeBackground(mode: 'felt' | 'app'): Promise<void> {
  await platformReady();
  const light = document.documentElement.getAttribute('data-theme') === 'light';
  const color = mode === 'felt'
    ? NATIVE_BG_FELT
    : (light ? NATIVE_BG_APP_LIGHT : NATIVE_BG_APP_DARK);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.nativeBg = mode;
  }
  if (!state.available) return;
  try {
    await NativeBackground.setColor({ color });
  } catch {
    // web / plugin absent
  }
}

/**
 * WebView plein écran : la barre de statut se superpose, le CSS gère
 * les insets. Style du texte selon le thème.
 */
export async function configureStatusBar(): Promise<void> {
  await platformReady();
  if (!state.available) return;

  try {
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch {
    // ignore : non supporté (web / anciennes versions)
  }

  applyStatusBarFromTheme();
  window.setTimeout(applyStatusBarFromTheme, 0);
  window.setTimeout(applyStatusBarFromTheme, 400);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) applyStatusBarFromTheme();
  });
}
